import * as echarts from 'echarts';
import React, { useEffect, useRef, useState } from 'react';
import { ChartIcons, FilterIcons, XIcons } from '../../../icons/icons';
import 'echarts-gl';
import { MainLayout } from '../../../ui';
import {
  Applicant,
  CandibankJobApplicantRequest,
  JobApplicantSearchRequest,
  Skill,
} from '../../../utils/types/JobOpenings';
import { GraphFilterDrawer } from './GraphFilterDrawer';

interface AnalyticsDialogProps {
    isOpen: boolean,
    onClose: () => void,
    totalCandidates: number,
    searchObject: CandibankJobApplicantRequest,
    setSearchObject: (value: (((prevState: CandibankJobApplicantRequest) => CandibankJobApplicantRequest) | CandibankJobApplicantRequest)) => void,
    candidates: Applicant[],
    loading: boolean,
    setLoading: (value: (((prevState: boolean) => boolean) | boolean)) => void,
    extractedSkill: Skill[],
    setExtractedSkill: (value: (((prevState: Skill[]) => Skill[]) | Skill[])) => void,
    extractedJobTitles: Skill[],
    setExtractedJobTitles: (value: (((prevState: Skill[]) => Skill[]) | Skill[])) => void,
    extractedKeywords: Skill[],
    setExtractedKeywords: (value: (((prevState: Skill[]) => Skill[]) | Skill[])) => void,
    extractedBehavioralScore: Skill[],
    setExtractedBehavioralScore: (value: (((prevState: Skill[]) => Skill[]) | Skill[])) => void
}

export const AnalyticsDialog: React.FC<AnalyticsDialogProps> = ({
  isOpen,
  onClose,
  totalCandidates,
  searchObject,
  setSearchObject,
  candidates,
  loading,
  setLoading,
  extractedSkill,
  setExtractedSkill,
  extractedJobTitles,
  setExtractedJobTitles,
  extractedKeywords,
  setExtractedKeywords,
  extractedBehavioralScore,
  setExtractedBehavioralScore,
}) => {
  const [viewType, setViewType] = useState<'2d-chart' | '3d-chart'>('2d-chart');
  const [isAnalyticalFilter, setIsAnalyticalFilter] = useState(false);
  const chart2DRef = useRef<HTMLDivElement>(null);
  const chart3DRef = useRef<HTMLDivElement>(null);
  const chartInstance2D = useRef<echarts.ECharts | null>(null);
  const chartInstance3D = useRef<echarts.ECharts | null>(null);
  // const [extractedSkill, setExtractedSkill] = useState<Skill[]>([]);
  const [similarToggle, setSimilarToggle] = useState<boolean>(false);
  const [weightingEnabled, setWeightingEnabled] = useState<boolean>(false);
  // const [extractedJobTitles, setExtractedJobTitles] = useState<Skill[]>([]);
  const [similarJobTitle, setSimilarJobTitle] = useState<boolean>(false);
  const [similarBehavior, setSimilarBehavior] = useState<boolean>(false);
  // const [extractedBehavioralScore, setExtractedBehavioralScore] = useState<
  //     Skill[]
  // >([]);
  const [keywordToggle, setKeywordtoggle] = useState<boolean>(false);
  const [keywordWeightage, setKeywordWeightage] = useState<boolean>(true);
  // const [extractedKeywords, setExtractedKeywords] = useState<Skill[]>([]);
  const [useSkillPercentage, setUseSkillPercentage] = useState<boolean>(false);
  const [minMaxAge, setMinMaxAge] = useState<number[]>([0, 100]);
  const [statusList, setStatusList] = useState<string[]>([]);
  const [ageToggle, setAgeToggle] = useState<boolean>(true);
  const [genderList, setGenderList] = useState<string[]>([]);

  if (!isOpen) return null;

  // Mock data for scatter charts - candidate points
  const mockCandidateData = [
    { id: 1, experience: 2, skillsScore: 75, matchedScore: 85 },
    { id: 2, experience: 5, skillsScore: 88, matchedScore: 92 },
    { id: 3, experience: 3, skillsScore: 65, matchedScore: 78 },
    { id: 4, experience: 7, skillsScore: 92, matchedScore: 95 },
    { id: 5, experience: 1, skillsScore: 55, matchedScore: 60 },
    { id: 6, experience: 4, skillsScore: 80, matchedScore: 88 },
    { id: 7, experience: 6, skillsScore: 85, matchedScore: 90 },
    { id: 8, experience: 2, skillsScore: 70, matchedScore: 75 },
    { id: 9, experience: 8, skillsScore: 95, matchedScore: 98 },
    { id: 10, experience: 3, skillsScore: 72, matchedScore: 82 },
    { id: 11, experience: 5, skillsScore: 78, matchedScore: 85 },
    { id: 12, experience: 4, skillsScore: 83, matchedScore: 87 },
    { id: 13, experience: 6, skillsScore: 90, matchedScore: 93 },
    { id: 14, experience: 1, skillsScore: 45, matchedScore: 55 },
    { id: 15, experience: 9, skillsScore: 97, matchedScore: 99 },
  ];

  // Mock data for skills chart
  const mockChartData = [
    { skill: 'JavaScript', count: 1247 },
    { skill: 'React', count: 1089 },
    { skill: 'Python', count: 967 },
    { skill: 'TypeScript', count: 834 },
    { skill: 'Node.js', count: 756 },
  ];

  // Mock data for locations
  const mockLocationData = [
    { location: 'San Francisco, CA', count: 423 },
    { location: 'New York, NY', count: 387 },
    { location: 'Seattle, WA', count: 342 },
    { location: 'Austin, TX', count: 298 },
    { location: 'Boston, MA', count: 267 },
  ];

  // Initialize 2D Chart
  useEffect(() => {
    if (viewType === '2d-chart' && chart2DRef.current && candidates.length > 0) {
      const timer = setTimeout(() => {
        if (chartInstance2D.current) {
          chartInstance2D.current.dispose();
          chartInstance2D.current = null;
        }

        chartInstance2D.current = echarts.init(chart2DRef.current);

        // Dynamically compute axis ranges from actual data
        const xValues = candidates.map(c => c.value[0]);
        const yValues = candidates.map(c => c.value[1]);
        const zValues = candidates.map(c => c.value[2]);

        const xMin = Math.floor(Math.min(...xValues));
        const xMax = Math.ceil(Math.max(...xValues));
        const yMin = Math.floor(Math.min(...yValues));
        const yMax = Math.ceil(Math.max(...yValues));
        const zMin = Math.floor(Math.min(...zValues));
        const zMax = Math.ceil(Math.max(...zValues));

        const option = {
          title: {
            text: '2D Scatter: Skills vs Experience',
            left: 'center',
            top: 20,
            textStyle: {
              fontSize: 18,
              fontWeight: 'bold',
              color: '#334155',
            },
          },
          tooltip: {
            trigger: 'item',
            formatter: function (params: any) {
              const data = params.data;
              const candidate = candidates[params.dataIndex];
              return `<div style="padding: 8px;">
                            <strong>${candidate?.name || `Candidate ${params.dataIndex + 1}`}</strong><br/>
                            Skills: ${data[0]}<br/>
                            Experience: ${data[1]} years<br/>
                            Behavior Count: ${data[2]}
                        </div>`;
            },
            backgroundColor: 'rgba(255, 255, 255, 0.95)',
            borderColor: '#e2e8f0',
            borderWidth: 1,
            textStyle: { color: '#334155' },
          },
          grid: {
            left: '8%',
            right: '20%',
            top: '15%',
            bottom: '15%',
            containLabel: true,
          },
          xAxis: {
            type: 'value',
            name: 'Skills',
            nameLocation: 'center',
            nameGap: 40,
            nameTextStyle: {
              color: '#475569',
              fontSize: 14,
              fontWeight: 'bold',
            },
            // Use dynamic min/max instead of hardcoded values
            min: xMin,
            max: xMax,
            splitLine: {
              show: true,
              lineStyle: { color: '#e2e8f0', opacity: 0.8 },
            },
            axisLabel: { color: '#64748b', fontSize: 12 },
          },
          yAxis: {
            type: 'value',
            name: 'Experience (Years)',
            nameLocation: 'center',
            nameGap: 50,
            nameTextStyle: {
              color: '#475569',
              fontSize: 14,
              fontWeight: 'bold',
            },
            // Use dynamic min/max instead of hardcoded values
            min: yMin,
            max: yMax,
            splitLine: {
              show: true,
              lineStyle: { color: '#e2e8f0', opacity: 0.8 },
            },
            axisLabel: { color: '#64748b', fontSize: 12 },
          },
          visualMap: {
            min: yMin,
            max: yMax,
            dimension: 1,
            orient: 'vertical',
            right: 20,
            top: 80,
            text: ['High', 'Low'],
            calculable: true,
            textStyle: {
              color: '#64748b',
            },
            inRange: {
              color: ['#ef4444', '#f59e0b', '#3b82f6', '#10b981'],
            },
          },
          series: [{
            name: 'Candidates',
            type: 'scatter',
            // Explicitly map the data
            data: candidates.map(c => [c.value[0], c.value[1], c.value[2]]),
            symbolSize: function (data: any) {
              return 25;
            },
            itemStyle: {
              opacity: 0.8,
              borderWidth: 2,
              borderColor: '#ffffff',
            },
            emphasis: {
              itemStyle: {
                opacity: 1,
                borderWidth: 3,
                shadowBlur: 10,
                shadowColor: 'rgba(0,0,0,0.3)',
              },
              scale: 1.2,
            },
            animation: true,
            animationDuration: 1200,
            animationEasing: 'cubicOut',
          }],
        };

        chartInstance2D.current.setOption(option, true);

        // Force resize after setting options
        chartInstance2D.current.resize();

      }, 100);

      return () => clearTimeout(timer);
    }
  }, [viewType, candidates]);

  // Initialize 3D Chart
  useEffect(() => {
    if (viewType === '3d-chart' && chart3DRef.current && candidates.length > 0) {
      const timer = setTimeout(() => {
        if (chartInstance3D.current) {
          chartInstance3D.current.dispose();
          chartInstance3D.current = null;
        }
        try {
          chartInstance3D.current = echarts.init(chart3DRef.current, null, {
            renderer: 'canvas',
          });
        } catch (error) {
          return;
        }

        const xValues = candidates.map(c => c.value[0]);
        const yValues = candidates.map(c => c.value[1]);
        const zValues = candidates.map(c => c.value[2]);

        const xMin = Math.floor(Math.min(...xValues));
        const xMax = Math.ceil(Math.max(...xValues));
        const yMin = Math.floor(Math.min(...yValues));
        const yMax = Math.ceil(Math.max(...yValues));
        const zMin = Math.floor(Math.min(...zValues));
        const zMax = Math.ceil(Math.max(...zValues));

        const option = {
          title: {
            text: '3D Scatter: Skills Score vs Experience vs Match Score',
            left: 'center',
            top: 20,
            textStyle: {
              fontSize: 18,
              fontWeight: 'bold',
              color: '#334155',
            },
          },
          tooltip: {
            trigger: 'item',
            formatter: function (params: any) {
              const data = params.data;
              const candidate = candidates[params.dataIndex];
              return `<div style="padding: 8px;">
                            <strong>${candidate?.name || `Candidate ${params.dataIndex + 1}`}</strong><br/>
                            Skills: ${data[0]}<br/>
                            Experience: ${data[1]} years<br/>
                            Behavior Count: ${data[2]}
                        </div>`;
            },
            backgroundColor: 'rgba(255, 255, 255, 0.95)',
            borderColor: '#e2e8f0',
            borderWidth: 1,
            textStyle: { color: '#334155' },
          },
          visualMap: {
            min: yMin,
            max: yMax,
            dimension: 1,
            orient: 'vertical',
            right: 20,
            top: 80,
            text: ['High', 'Low'],
            calculable: true,
            textStyle: { color: '#64748b' },
            inRange: {
              color: ['#ef4444', '#f59e0b', '#3b82f6', '#10b981'],
            },
          },
          xAxis3D: {
            type: 'value',
            name: 'Skills',
            nameTextStyle: {
              color: '#1e293b',
              fontSize: 14,
              fontWeight: 'bold',
            },
            min: xMin,
            max: xMax,
            axisLabel: {
              textStyle: {
                color: '#334155',
                fontSize: 12,
                fontWeight: '500',
              },
            },
            splitLine: {
              show: true,
              lineStyle: { color: '#cbd5e1', opacity: 0.8 },
            },
          },
          yAxis3D: {
            type: 'value',
            name: 'Experience (Years)',
            nameTextStyle: {
              color: '#1e293b',
              fontSize: 14,
              fontWeight: 'bold',
            },
            min: yMin,
            max: yMax,
            axisLabel: {
              textStyle: {
                color: '#334155',
                fontSize: 12,
                fontWeight: '500',
              },
            },
            splitLine: {
              show: true,
              lineStyle: { color: '#cbd5e1', opacity: 0.8 },
            },
          },
          zAxis3D: {
            type: 'value',
            name: 'Behavior Count',
            nameTextStyle: {
              color: '#1e293b',
              fontSize: 14,
              fontWeight: 'bold',
            },
            min: zMin,
            max: zMax,
            axisLabel: {
              textStyle: {
                color: '#334155',
                fontSize: 12,
                fontWeight: '500',
              },
            },
            splitLine: {
              show: true,
              lineStyle: { color: '#cbd5e1', opacity: 0.8 },
            },
          },
          grid3D: {
            boxWidth: 180,
            boxHeight: 120,
            boxDepth: 100,
            show: true,
            environment: 'none',
            viewControl: {
              projection: 'perspective',
              autoRotate: false,
              rotateSensitivity: 2,
              zoomSensitivity: 2,
              panSensitivity: 2,
              alpha: 25,
              beta: 45,
              distance: 280,
              minDistance: 150,
              maxDistance: 400,
            },
            light: {
              main: {
                intensity: 1.5,
                shadow: true,
                shadowQuality: 'medium',
                alpha: 30,
                beta: 40,
              },
              ambient: { intensity: 0.4 },
            },
            postEffect: {
              enable: true,
              SSAO: {
                enable: true,
                quality: 'medium',
                radius: 2,
              },
            },
          },
          series: [{
            name: 'Candidates',
            type: 'scatter3D',
            data: candidates.map(c => [c.value[0], c.value[1], c.value[2]]),
            symbolSize: 25,
            itemStyle: {
              opacity: 0.8,
              borderWidth: 2,
              borderColor: '#ffffff',
            },
            emphasis: {
              itemStyle: {
                opacity: 1,
                borderWidth: 3,
                borderColor: '#ffffff',
              },
            },
            animation: true,
            animationDurationUpdate: 1500,
            animationEasingUpdate: 'cubicInOut',
          }],
        };

        try {
          chartInstance3D.current.setOption(option, true);
          chartInstance3D.current.resize();
        } catch (error) {
          console.error('Failed to set 3D chart option:', error);
        }
      }, 200);

      return () => clearTimeout(timer);
    }
  }, [viewType, candidates]);

  // Cleanup charts on unmount
  useEffect(() => {
    return () => {
      if (chartInstance2D.current) {
        chartInstance2D.current.dispose();
        chartInstance2D.current = null;
      }
      if (chartInstance3D.current) {
        chartInstance3D.current.dispose();
        chartInstance3D.current = null;
      }
    };
  }, []);

  // Additional cleanup when switching views to prevent overlaps
  useEffect(() => {
    // Immediately dispose the inactive chart when switching
    if (viewType === '2d-chart' && chartInstance3D.current) {
      chartInstance3D.current.dispose();
      chartInstance3D.current = null;
    }
    if (viewType === '3d-chart' && chartInstance2D.current) {
      chartInstance2D.current.dispose();
      chartInstance2D.current = null;
    }
  }, [viewType]);

  // Resize charts when view changes
  useEffect(() => {
    const handleResize = () => {
      chartInstance2D.current?.resize();
      chartInstance3D.current?.resize();
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (viewType === '2d-chart' && chartInstance2D.current) {
        chartInstance2D.current.resize();
      }
      if (viewType === '3d-chart' && chartInstance3D.current) {
        chartInstance3D.current.resize();
      }
    }, 200);

    return () => clearTimeout(timer);
  }, [viewType]);

  return (
    <div
      className='fixed inset-0 bg-slate-100 flex flex-col w-screen h-screen'
      style={{ zIndex: 99999 }}
    >
      { /* Header */ }
      <div className='bg-white border-b border-slate-200 px-6 py-3.5 shadow-xs flex-shrink-0'>
        <div className='w-full flex items-center justify-between'>
          <div className='flex items-center gap-3'>
            <div className='w-9 h-9 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl flex items-center justify-center shadow-md shadow-indigo-200'>
              <ChartIcons className='w-5 h-5 text-white'/>
            </div>
            <div>
              <h2 className='text-base font-bold text-slate-900'>Candidate Analytics</h2>
              <p className='text-xs text-slate-500 font-medium'>
                Explore insights from { candidates.length } candidates
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className='p-1.5 hover:bg-slate-100 rounded-full transition-colors text-slate-400 hover:text-slate-700'
            title='Close Analytics'
          >
            <XIcons className='w-5 h-5'/>
          </button>
        </div>
      </div>

      { /* View Type Toggle */ }
      <div className='bg-white/90 border-b border-slate-200/80 px-6 py-2.5 flex-shrink-0'>
        <div className='w-full flex items-center justify-between'>
          <div className='flex items-center gap-2'>
            <button
              onClick={() => setViewType('2d-chart')}
              className={`px-4 py-1.5 rounded-lg font-bold text-xs transition-all duration-200 ${
                viewType === '2d-chart'
                  ? 'bg-indigo-600 text-white shadow-sm'
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
            >
              2D Chart
            </button>
            <button
              onClick={() => setViewType('3d-chart')}
              className={`px-4 py-1.5 rounded-lg font-bold text-xs transition-all duration-200 ${
                viewType === '3d-chart'
                  ? 'bg-indigo-600 text-white shadow-sm'
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
            >
              3D Chart
            </button>
          </div>
          <button
            onClick={() => setIsAnalyticalFilter(!isAnalyticalFilter)}
            className='flex items-center gap-1.5 px-3 py-1.5 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 hover:shadow-xs transition-all duration-200 font-bold text-xs text-slate-600'
          >
            <FilterIcons className='w-3.5 h-3.5'/>
            Filters
          </button>
        </div>
      </div>

      { /* Main Chart Area */ }
      <div className='flex-1 w-full p-4 sm:p-6 overflow-hidden min-h-0 flex flex-col'>
        <div className='w-full flex-1 bg-white rounded-2xl border border-slate-200 shadow-sm p-4 sm:p-6 flex flex-col min-h-0 relative'>
          { loading && (
            <div className='absolute inset-0 bg-white/70 backdrop-blur-xs z-10 flex flex-col items-center justify-center rounded-2xl'>
              <div className='w-8 h-8 border-3 border-indigo-600 border-t-transparent rounded-full animate-spin'></div>
              <p className='text-xs font-bold text-slate-600 mt-2'>Loading Analytics...</p>
            </div>
          ) }

          { viewType === '2d-chart' ? (
            <div
              ref={chart2DRef}
              className='w-full h-full flex-1 min-h-[380px]'
            />
          ) : (
            <div
              ref={chart3DRef}
              className='w-full h-full flex-1 min-h-[380px]'
            />
          ) }
        </div>
      </div>

      <GraphFilterDrawer
        isOpen={isAnalyticalFilter}
        onClose={() => setIsAnalyticalFilter(false)}
        extractedSkill={extractedSkill}
        setExtractedSkill={setExtractedSkill}
        weightingEnabled={weightingEnabled}
        setWeightingEnabled={setWeightingEnabled}
        similarToggle={similarToggle}
        setSimilarToggle={setSimilarToggle}
        similarJobTitle={similarJobTitle}
        setSimilarJobTitle={setSimilarJobTitle}
        extractedJobTitles={extractedJobTitles}
        setExtractedJobTitles={setExtractedJobTitles}
        extractedBehavioralScore={extractedBehavioralScore}
        setExtractedBehavioralScore={setExtractedBehavioralScore}
        similarBehavior={similarBehavior}
        setSimilarBehavior={setSimilarBehavior}
        extractedKeywords={extractedKeywords}
        setExtractedKeywords={setExtractedKeywords}
        keywordWeightage={keywordWeightage}
        setKeywordWeightage={setKeywordWeightage}
        keywordToggle={keywordToggle}
        setKeywordToggle={setKeywordtoggle}
        searchObject={searchObject}
        setSearchObject={setSearchObject}
        genderList={genderList}
        setGenderList={setGenderList}
        minMaxAge={minMaxAge}
        setMinMaxAge={setMinMaxAge}
        ageToggle={ageToggle}
        setAgeToggle={setAgeToggle}
        setUseSkillPercentage={setUseSkillPercentage}
        statusList={statusList}
        loading={loading}
        setLoading={setLoading}
      />

      { /* Footer */ }
      <div className='bg-white border-t border-slate-200 px-6 py-3 shadow-xs flex-shrink-0'>
        <div className='w-full flex items-center justify-between'>
          <p className='text-xs text-slate-500 font-medium'>
            Data refreshed • Full Screen Analytics View
          </p>
          <button
            onClick={onClose}
            className='px-6 py-2 bg-indigo-600 text-white font-bold text-xs rounded-xl hover:bg-indigo-700 transition-all duration-200 shadow-md shadow-indigo-200'
          >
            Close Analytics
          </button>
        </div>
      </div>
    </div>
  );
};
